import type { AccountCloudPosture } from './accountStateMachine.js'

export type CapabilitiesInput = {
  hasMountedAccount: boolean
  hasAcceptedSession: boolean
  hasVerifiedCloudAuthority: boolean
  cloudPosture: AccountCloudPosture
  hasSyncAccess: boolean
  isTerminationScheduled: boolean
  isDeactivated: boolean
  hasUsername: boolean
  isSyncPaired: boolean
  isIndexSpace: boolean
  isViewMode: boolean
}

export type Capabilities = {
  hasMountedAccount: boolean
  hasAcceptedSession: boolean
  hasVerifiedCloudAuthority: boolean
  cloudPosture: AccountCloudPosture
  hasSyncAccess: boolean
  hasUsername: boolean
  isSyncPaired: boolean
  isIndexSpace: boolean
  isViewMode: boolean
  canOpenGoSurface: boolean
  canOpenSyncSurface: boolean
  canManageAccount: boolean
  canShowAccountActions: boolean
  canLogout: boolean
  canRenameUser: boolean
  canActivateSync: boolean
  canTerminateSync: boolean
  canDeleteAccount: boolean
  canOpenIndex: boolean
  canManageSpace: boolean
  canRenameSpace: boolean
  canCreateSpace: boolean
  canDeleteSpace: boolean
  canSyncRead: boolean
  canSyncWrite: boolean
  canEditContent: boolean
  canEditStructure: boolean
}

export function resolveCapabilities(input: CapabilitiesInput): Capabilities {
  const canOpenGoSurface = true
  const canOpenSyncSurface = true
  const hasSyncLifecycle = input.hasMountedAccount && (input.hasSyncAccess || input.isDeactivated)
  const canManageAccount = input.hasVerifiedCloudAuthority && input.hasUsername && hasSyncLifecycle
  const canShowAccountActions = canManageAccount
  const canLogout = input.hasMountedAccount || input.hasAcceptedSession
  const canRenameUser = canShowAccountActions
  const canActivateSync = !input.hasMountedAccount
    || (
      input.hasVerifiedCloudAuthority
      && (!input.hasSyncAccess || input.isTerminationScheduled || input.isDeactivated)
    )
  const canTerminateSync = canShowAccountActions && input.hasSyncAccess && !input.isTerminationScheduled
  const canDeleteAccount =
    input.hasVerifiedCloudAuthority
    && input.hasUsername
    && (!input.hasSyncAccess || input.isTerminationScheduled || input.isDeactivated)
  const canOpenIndex =
    !input.isDeactivated
    || (input.hasMountedAccount && input.hasUsername && hasSyncLifecycle)
  const canManageSpace = canOpenIndex && !input.isIndexSpace && !input.isViewMode && !input.isDeactivated
  const canRenameSpace = canManageSpace
  const canCreateSpace = canOpenIndex && !input.isDeactivated
  const canDeleteSpace = canManageSpace
  const canSyncRead =
    input.cloudPosture === 'connected-verified'
    && input.hasUsername
    && hasSyncLifecycle
  const canSyncWrite = canSyncRead && input.hasSyncAccess && input.isSyncPaired && !input.isIndexSpace && !input.isViewMode
  const canEditContent = !input.isViewMode && !input.isDeactivated
  const canEditStructure = !input.isViewMode && !input.isDeactivated
  return {
    hasMountedAccount: input.hasMountedAccount,
    hasAcceptedSession: input.hasAcceptedSession,
    hasVerifiedCloudAuthority: input.hasVerifiedCloudAuthority,
    cloudPosture: input.cloudPosture,
    hasSyncAccess: input.hasSyncAccess,
    hasUsername: input.hasUsername,
    isSyncPaired: input.isSyncPaired,
    isIndexSpace: input.isIndexSpace,
    isViewMode: input.isViewMode,
    canOpenGoSurface,
    canOpenSyncSurface,
    canManageAccount,
    canShowAccountActions,
    canLogout,
    canRenameUser,
    canActivateSync,
    canTerminateSync,
    canDeleteAccount,
    canOpenIndex,
    canManageSpace,
    canRenameSpace,
    canCreateSpace,
    canDeleteSpace,
    canSyncRead,
    canSyncWrite,
    canEditContent,
    canEditStructure
  }
}
